import type { ReactNode } from 'react';

const WEEKDAY_LABELS = ['M', 'D', 'M', 'D', 'F', 'S', 'S'];

export interface MonthGridDay {
  /** ISO-Tag, z. B. "2026-08-20". */
  iso: string;
  /** Tag im Monat, 1–31. */
  day: number;
}

/** Was für einen Tag gerendert werden soll. */
export interface MonthGridCell {
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  ariaLabel?: string;
  onClick?: () => void;
}

/**
 * Monatskalender als 7-Spalten-Raster — reine Anordnung, ohne Bedeutung.
 *
 * Aus `InseratDetail.MonthHeatmap` herausgezogen, damit die Detailseite
 * (Kategorie-Farben, Bereichsauswahl) und die Daten-Seite (Belegungs-Wertskala)
 * dieselbe Geometrie teilen, ohne dass eine der beiden der anderen ihre
 * Semantik aufzwingt. Was eine Zelle bedeutet, entscheidet `renderDay`.
 */
export default function MonthGrid({
  year,
  monthIdx,
  label,
  renderDay,
  footer,
}: {
  year: number;
  /** 0 = Januar. */
  monthIdx: number;
  label: string;
  renderDay: (day: MonthGridDay) => MonthGridCell;
  footer?: ReactNode;
}) {
  const firstDay = new Date(year, monthIdx, 1);
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  // ISO-Wochentag: Mo=0…So=6
  const startOffset = (firstDay.getDay() + 6) % 7;

  const cells: (MonthGridDay | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({
      iso: `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      day: d,
    });
  }

  return (
    <div className="rounded border border-gray-200 p-2">
      <div className="text-xs font-medium text-gray-700 mb-1.5">{label}</div>
      <div className="grid grid-cols-7 gap-px text-[8px] text-gray-400 mb-0.5">
        {WEEKDAY_LABELS.map((wd, i) => (
          <div key={i} className="text-center leading-none">{wd}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {cells.map((cell, i) => {
          if (cell === null) return <div key={`empty-${i}`} className="aspect-square" />;
          const spec = renderDay(cell);
          return (
            <button
              type="button"
              key={cell.iso}
              onClick={spec.onClick}
              disabled={!spec.onClick}
              className={`aspect-square rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                spec.onClick ? 'hover:brightness-110' : ''
              } ${spec.className ?? ''}`}
              style={spec.style}
              title={spec.title}
              aria-label={spec.ariaLabel ?? spec.title}
            />
          );
        })}
      </div>
      {footer}
    </div>
  );
}
